/**
 * @fileoverview Pivot field panel — the field list and drop zones behind the
 * table builder (MOR-906 §7.8)
 *
 * Purpose: compose a pivot spec the way a spreadsheet does. Every dimension
 * and measure is listed once on the left; dragging one into Filters, Columns,
 * Rows or Values is what shapes the table. Clicking its checkbox does the
 * obvious thing for people who would rather not drag.
 *
 * The engine groups two row levels and one column level (convex/timesheets/
 * pivot.ts), so the zones cap there: dropping onto a full zone swaps the
 * oldest field out rather than refusing the gesture. Rows and Values never
 * empty — a table with no rows or no cells is not a table.
 *
 * Filters are the same bracketed AND/OR expression the widget builder uses,
 * shown as one chip per condition while the expression is a plain series of
 * ANDs. Anything more (OR, brackets) is edited in the expression builder
 * underneath, which the Advanced toggle also opens on demand.
 *
 * @component
 * @example
 * <PivotFieldPanel spec={spec} onChange={patch} dimensions={dimensions} />
 */

import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  Columns3,
  Filter,
  GripVertical,
  Pin,
  Rows3,
  Sigma,
  X,
} from "lucide-react";

import type {
  TimePivotDimension,
  TimePivotMeasure,
  TimePivotSpec,
} from "@cvx/timesheets/savedPivotReports/schema";
import type { QueryField, QueryToken } from "@cvx/timesheets/query/entryQuery";

import { cn } from "@/utils/misc";
import { Input } from "@/ui/primitives/input";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  ConditionValueList,
  type EntryQueryDimension,
} from "./ConditionValuePicker";
import { EntryQueryBuilder } from "./EntryQueryBuilder";
import { DIMENSION_LABELS, MEASURE_LABELS } from "./pivotExport";

const DIMENSIONS: ReadonlyArray<TimePivotDimension> = [
  "grade",
  "user",
  "job",
  "category",
  "billable",
  "status",
  "writtenOff",
  "date",
  "week",
  "month",
];

const MEASURES: ReadonlyArray<TimePivotMeasure> = [
  "hours",
  "amount",
  "writeOff",
  "rate",
  "units",
  "entries",
];

/**
 * The dimensions a filter can test. The time axes are the reporting period,
 * which the toolbar sets, so they group but do not filter.
 */
const FILTERABLE = new Set<string>([
  "grade",
  "user",
  "job",
  "category",
  "billable",
  "status",
  "writtenOff",
]);

const ROW_CAP = 2;
const COLUMN_CAP = 1;

type ZoneId = "filters" | "columns" | "rows" | "values";

/** What a drag is carrying, and where it came from if it started in a zone. */
type DragData =
  | {
      kind: "dimension";
      key: TimePivotDimension;
      from?: ZoneId;
      index?: number;
    }
  | { kind: "measure"; key: TimePivotMeasure; from?: ZoneId; index?: number };

const CHIP =
  "inline-flex max-w-full items-center gap-1 rounded-md border border-border/60 bg-background py-1 pl-1.5 pr-0.5 text-xs";

const DRAG_HANDLE = "touch-none select-none cursor-grab active:cursor-grabbing";

/**
 * Prefer the zone under the pointer. `closestCenter` misses empty drop
 * boxes (their centre is further away than a neighbour's) so a drag into
 * Filters looks like it did nothing.
 */
const zoneCollision: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length > 0) return pointer;
  return rectIntersection(args);
};

const PICKER_ARM_MS = 120;

type Condition = Extract<QueryToken, { t: "cond" }>;

/**
 * The conditions of an AND-only expression, with the token index of each.
 *
 * @returns null when the expression uses OR or brackets, which chips cannot
 * represent — those are edited in the expression builder instead.
 */
function simpleConditions(
  tokens: readonly QueryToken[],
): Array<{ index: number; token: Condition }> | null {
  const out: Array<{ index: number; token: Condition }> = [];
  for (const [index, token] of tokens.entries()) {
    if (index % 2 === 0) {
      if (token.t !== "cond") return null;
      out.push({ index, token });
    } else if (token.t !== "and") {
      return null;
    }
  }
  return out;
}

/** Drop a condition and the AND that joined it to its neighbour. */
function withoutCondition(
  tokens: readonly QueryToken[],
  index: number,
): QueryToken[] {
  const next = [...tokens];
  if (index > 0) next.splice(index - 1, 2);
  else next.splice(0, next.length > 1 ? 2 : 1);
  return next;
}

/**
 * Place a field in a zone, moving it if it was already there.
 *
 * Over capacity, the oldest other field leaves rather than the drop being
 * refused: dragging a field onto a full zone reads as "use this one".
 */
function insertCapped<T extends string>(
  list: readonly T[],
  key: T,
  at: number | undefined,
  cap: number,
): T[] {
  const next = list.filter((item) => item !== key);
  next.splice(Math.min(at ?? next.length, next.length), 0, key);
  if (next.length > cap) {
    for (let i = next.length - 1; i >= 0; i -= 1) {
      if (next[i] !== key) {
        next.splice(i, 1);
        break;
      }
    }
  }
  return next;
}

export function PivotFieldPanel({
  spec,
  onChange,
  dimensions,
}: {
  spec: TimePivotSpec;
  onChange: (patch: Partial<TimePivotSpec>) => void;
  /** Filterable dimensions and the values each offers. */
  dimensions: ReadonlyArray<EntryQueryDimension>;
}) {
  const [search, setSearch] = useState("");
  const [dragging, setDragging] = useState<DragData | null>(null);
  const [advanced, setAdvanced] = useState(false);
  /** The condition being composed; `index` is null for a new one. */
  const [editing, setEditing] = useState<{
    field: QueryField;
    index: number | null;
    values: string[];
  } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (armTimer.current) clearTimeout(armTimer.current);
    },
    [],
  );

  const startNewFilter = (field: QueryField) => {
    setPickerOpen(false);
    setEditing({ field, index: null, values: [] });
    if (armTimer.current) clearTimeout(armTimer.current);
    armTimer.current = setTimeout(() => setPickerOpen(true), PICKER_ARM_MS);
  };

  const closeFilter = () => {
    setPickerOpen(false);
    setEditing(null);
  };

  // Mouse/touch sensors: PointerSensor is cancelled inside a Radix dialog
  // (react-remove-scroll + the field list's overflow). Distance keeps a
  // click on the checkbox from becoming a drag.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 6 },
    }),
  );

  const tokens = spec.filter ?? [];
  const conditions = simpleConditions(tokens);
  const showAdvanced = advanced || conditions === null;

  const setFilter = (next: QueryToken[]) =>
    onChange({ filter: next.length > 0 ? next : undefined });

  const place = (zone: ZoneId, data: DragData, at?: number) => {
    if (zone === "values") {
      if (data.kind !== "measure") return;
      onChange({
        measures: insertCapped(spec.measures, data.key, at, MEASURES.length),
      });
      return;
    }
    if (data.kind !== "dimension") return;

    if (zone === "filters") {
      if (!FILTERABLE.has(data.key)) return;
      startNewFilter(data.key as QueryField);
      return;
    }

    const dimension = data.key;
    if (zone === "rows") {
      const rows = insertCapped(spec.rows, dimension, at, ROW_CAP);
      onChange({
        rows,
        columns: spec.columns.filter((c) => c !== dimension),
        // Subtotals sum an inner level, so they need one to exist.
        showSubtotals: rows.length > 1 ? spec.showSubtotals : false,
      });
      return;
    }
    // The last row dimension cannot leave: a table needs something to group.
    if (spec.rows.length === 1 && spec.rows[0] === dimension) return;
    const rows = spec.rows.filter((r) => r !== dimension);
    onChange({
      columns: insertCapped(spec.columns, dimension, at, COLUMN_CAP),
      rows,
      showSubtotals: rows.length > 1 ? spec.showSubtotals : false,
    });
  };

  const remove = (zone: ZoneId, index: number) => {
    if (zone === "rows") {
      if (spec.rows.length <= 1) return;
      onChange({
        rows: spec.rows.filter((_, i) => i !== index),
        showSubtotals: false,
      });
      return;
    }
    if (zone === "columns") {
      onChange({ columns: spec.columns.filter((_, i) => i !== index) });
      return;
    }
    if (zone === "values") {
      if (spec.measures.length <= 1) return;
      const measure = spec.measures[index];
      const rowMeasures = spec.rowMeasures?.filter((m) => m !== measure);
      onChange({
        measures: spec.measures.filter((_, i) => i !== index),
        rowMeasures:
          rowMeasures && rowMeasures.length > 0 ? rowMeasures : undefined,
      });
      return;
    }
    setFilter(withoutCondition(tokens, index));
  };

  /**
   * Pin a measure beside the row labels instead of repeating it under every
   * column — Rate belongs next to the person, not in each category.
   */
  const togglePinned = (measure: TimePivotMeasure) => {
    const pinned = spec.rowMeasures ?? [];
    const next = pinned.includes(measure)
      ? pinned.filter((m) => m !== measure)
      : [...pinned, measure];
    onChange({ rowMeasures: next.length > 0 ? next : undefined });
  };

  const toggleDimension = (dimension: TimePivotDimension) => {
    if (spec.rows.includes(dimension)) {
      remove("rows", spec.rows.indexOf(dimension));
      return;
    }
    if (spec.columns.includes(dimension)) {
      remove("columns", spec.columns.indexOf(dimension));
      return;
    }
    // Checking a field fills Rows first, then Columns — the shape most
    // tables want, and the one a spreadsheet assumes.
    place(spec.rows.length < ROW_CAP ? "rows" : "columns", {
      kind: "dimension",
      key: dimension,
    });
  };

  const toggleMeasure = (measure: TimePivotMeasure) => {
    if (!spec.measures.includes(measure)) {
      place("values", { kind: "measure", key: measure });
      return;
    }
    remove("values", spec.measures.indexOf(measure));
  };

  const commitCondition = (negate: boolean) => {
    if (!editing || editing.values.length === 0) return;
    const condition: Condition = {
      t: "cond",
      field: editing.field,
      negate,
      values: editing.values,
    };
    if (editing.index === null) {
      // Imply AND only where one is legal: an expression left mid-edit in
      // the advanced builder may already end on an operator or a bracket.
      const last = tokens[tokens.length - 1];
      const needsJoin = !!last && (last.t === "cond" || last.t === "close");
      setFilter(
        needsJoin
          ? [...tokens, { t: "and" }, condition]
          : [...tokens, condition],
      );
    } else {
      const next = [...tokens];
      next[editing.index] = condition;
      setFilter(next);
    }
    closeFilter();
  };

  const dimensionFor = (field: QueryField) =>
    dimensions.find((d) => d.field === field);

  const matches = (label: string) =>
    label.toLowerCase().includes(search.trim().toLowerCase());

  const handleDragStart = (event: DragStartEvent) =>
    setDragging(event.active.data.current as DragData);

  const handleDragEnd = (event: DragEndEvent) => {
    setDragging(null);
    const data = event.active.data.current as DragData | undefined;
    const target = event.over?.data.current as
      { zone: ZoneId; index?: number } | undefined;
    if (!data || !target) return;
    place(target.zone, data, target.index);
  };

  /** Which zones would accept what is currently being dragged. */
  const accepts = (zone: ZoneId): boolean => {
    if (!dragging) return false;
    if (zone === "values") return dragging.kind === "measure";
    if (dragging.kind !== "dimension") return false;
    if (zone === "filters") return FILTERABLE.has(dragging.key);
    return true;
  };

  const draggedLabel = dragging
    ? dragging.kind === "measure"
      ? MEASURE_LABELS[dragging.key]
      : DIMENSION_LABELS[dragging.key]
    : "";

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={zoneCollision}
      autoScroll={false}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 lg:grid-cols-[220px_1fr]">
          {/* Field list */}
          <div className="flex flex-col gap-2 rounded-lg border border-border/70 p-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search fields…"
              className="h-7 text-xs"
            />
            <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
              <FieldGroup label="Group by" />
              {DIMENSIONS.filter((d) => matches(DIMENSION_LABELS[d])).map(
                (dimension) => (
                  <FieldItem
                    key={dimension}
                    data={{ kind: "dimension", key: dimension }}
                    label={DIMENSION_LABELS[dimension]}
                    checked={
                      spec.rows.includes(dimension) ||
                      spec.columns.includes(dimension)
                    }
                    onToggle={() => toggleDimension(dimension)}
                  />
                ),
              )}
              <FieldGroup label="Measure" />
              {MEASURES.filter((m) => matches(MEASURE_LABELS[m])).map(
                (measure) => (
                  <FieldItem
                    key={measure}
                    data={{ kind: "measure", key: measure }}
                    label={MEASURE_LABELS[measure]}
                    checked={spec.measures.includes(measure)}
                    onToggle={() => toggleMeasure(measure)}
                  />
                ),
              )}
            </div>
          </div>

          {/* Drop zones */}
          <div className="grid gap-2 sm:grid-cols-2">
            <Zone
              zone="filters"
              label="Filters"
              icon={Filter}
              hint="Keep only some rows — drag a Group by field here, or use Add"
              accepts={accepts("filters")}
              dragging={!!dragging}
              action={
                <span className="flex items-center gap-2">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                      >
                        Add
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {DIMENSIONS.filter((d) => FILTERABLE.has(d)).map((d) => (
                        <DropdownMenuItem
                          key={d}
                          onClick={() => startNewFilter(d as QueryField)}
                        >
                          {DIMENSION_LABELS[d]}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    type="button"
                    className={cn(
                      "text-xs underline-offset-2 hover:underline",
                      showAdvanced ? "text-primary" : "text-muted-foreground",
                    )}
                    onClick={() => setAdvanced((prev) => !prev)}
                    disabled={conditions === null}
                    title={
                      conditions === null
                        ? "This expression uses OR or brackets, so it is edited below"
                        : "Edit the filter as a bracketed AND/OR expression"
                    }
                  >
                    Advanced
                  </button>
                </span>
              }
              empty={
                conditions !== null &&
                conditions.length === 0 &&
                editing === null
              }
            >
              {conditions === null ? (
                <span className="text-xs text-muted-foreground">
                  Bracketed expression — edit it below.
                </span>
              ) : (
                conditions.map(({ index, token }) => (
                  <ConditionChip
                    key={index}
                    dimension={dimensionFor(token.field)}
                    token={token}
                    open={pickerOpen && editing?.index === index}
                    onOpenChange={(open) => {
                      if (open) {
                        setEditing({
                          field: token.field,
                          index,
                          values: token.values,
                        });
                        setPickerOpen(true);
                        return;
                      }
                      closeFilter();
                    }}
                    values={editing?.index === index ? editing.values : []}
                    onValuesChange={(values) =>
                      setEditing((prev) => (prev ? { ...prev, values } : prev))
                    }
                    onApply={commitCondition}
                    onRemove={() => remove("filters", index)}
                  />
                ))
              )}
              {editing?.index === null && (
                <ConditionChip
                  dimension={dimensionFor(editing.field)}
                  open={pickerOpen}
                  onOpenChange={(open) => !open && closeFilter()}
                  values={editing.values}
                  onValuesChange={(values) =>
                    setEditing((prev) => (prev ? { ...prev, values } : prev))
                  }
                  onApply={commitCondition}
                  onRemove={closeFilter}
                />
              )}
            </Zone>

            <Zone
              zone="columns"
              label="Columns"
              icon={Columns3}
              hint="One field across the top"
              accepts={accepts("columns")}
              dragging={!!dragging}
              empty={spec.columns.length === 0}
            >
              {spec.columns.map((dimension, index) => (
                <FieldChip
                  key={dimension}
                  zone="columns"
                  index={index}
                  data={{
                    kind: "dimension",
                    key: dimension,
                    from: "columns",
                    index,
                  }}
                  label={DIMENSION_LABELS[dimension]}
                  onRemove={() => remove("columns", index)}
                />
              ))}
            </Zone>

            <Zone
              zone="rows"
              label="Rows"
              icon={Rows3}
              hint="Up to two levels down the side"
              accepts={accepts("rows")}
              dragging={!!dragging}
              empty={spec.rows.length === 0}
            >
              {spec.rows.map((dimension, index) => (
                <FieldChip
                  key={dimension}
                  zone="rows"
                  index={index}
                  data={{
                    kind: "dimension",
                    key: dimension,
                    from: "rows",
                    index,
                  }}
                  label={DIMENSION_LABELS[dimension]}
                  note={index === 1 ? "then by" : undefined}
                  onRemove={
                    spec.rows.length > 1
                      ? () => remove("rows", index)
                      : undefined
                  }
                />
              ))}
            </Zone>

            <Zone
              zone="values"
              label="Values"
              icon={Sigma}
              hint="What fills the cells"
              accepts={accepts("values")}
              dragging={!!dragging}
              empty={spec.measures.length === 0}
            >
              {spec.measures.map((measure, index) => (
                <FieldChip
                  key={measure}
                  zone="values"
                  index={index}
                  data={{
                    kind: "measure",
                    key: measure,
                    from: "values",
                    index,
                  }}
                  label={MEASURE_LABELS[measure]}
                  pinned={spec.rowMeasures?.includes(measure)}
                  onPin={() => togglePinned(measure)}
                  onRemove={
                    spec.measures.length > 1
                      ? () => remove("values", index)
                      : undefined
                  }
                />
              ))}
            </Zone>
          </div>
        </div>

        {showAdvanced && (
          <div className="border-t border-border/50 pt-3">
            <EntryQueryBuilder
              tokens={tokens}
              onChange={setFilter}
              dimensions={dimensions}
            />
          </div>
        )}
      </div>

      <DragOverlay dropAnimation={null} style={{ zIndex: 200 }}>
        {dragging && (
          <span className={cn(CHIP, "pr-1.5 shadow-md")}>{draggedLabel}</span>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function FieldGroup({ label }: { label: string }) {
  return (
    <p className="px-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
      {label}
    </p>
  );
}

function FieldItem({
  data,
  label,
  checked,
  onToggle,
}: {
  data: DragData;
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `field:${data.kind}:${data.key}`,
    data,
  });

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md px-1 py-1 text-xs hover:bg-muted/60",
        isDragging && "opacity-40",
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        aria-label={label}
      />
      <button
        ref={setNodeRef}
        type="button"
        className={cn(
          "flex min-w-0 flex-1 items-center gap-1 text-left",
          DRAG_HANDLE,
        )}
        title="Drag into Filters, Columns, Rows or Values"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3 shrink-0 text-muted-foreground/50" />
        <span className="truncate">{label}</span>
      </button>
    </div>
  );
}

function Zone({
  zone,
  label,
  icon: Icon,
  hint,
  accepts,
  dragging,
  empty,
  action,
  children,
}: {
  zone: ZoneId;
  label: string;
  icon: typeof Filter;
  hint: string;
  accepts: boolean;
  dragging: boolean;
  empty: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `zone:${zone}`,
    data: { zone },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex min-h-[76px] flex-col gap-1.5 rounded-lg border border-dashed p-2 transition-colors",
        isOver && accepts
          ? "border-primary bg-primary/5"
          : "border-border/70 bg-muted/30",
        dragging && !accepts && "opacity-50",
      )}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="size-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-1">{children}</div>
      {empty && (
        <span className="text-xs italic text-muted-foreground/70">{hint}</span>
      )}
    </div>
  );
}

function FieldChip({
  zone,
  index,
  data,
  label,
  note,
  pinned,
  onPin,
  onRemove,
}: {
  zone: ZoneId;
  index: number;
  data: DragData;
  label: string;
  /** Reads the chip's role in the zone, e.g. the second row level. */
  note?: string;
  pinned?: boolean;
  onPin?: () => void;
  /** Absent when the chip cannot leave — the last row or the last value. */
  onRemove?: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `chip:${zone}:${data.key}`,
    data,
  });
  // Dropping onto a chip inserts at its position, which is how a field is
  // moved between the outer and inner row levels.
  const { setNodeRef: setSlotRef } = useDroppable({
    id: `slot:${zone}:${index}`,
    data: { zone, index },
  });

  return (
    <span
      ref={(node) => {
        setNodeRef(node);
        setSlotRef(node);
      }}
      className={cn(CHIP, isDragging && "opacity-40")}
    >
      {note && <span className="text-muted-foreground">{note}</span>}
      <button
        type="button"
        className={cn("flex items-center gap-1", DRAG_HANDLE)}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3 text-muted-foreground/50" />
        <span className="font-medium">{label}</span>
      </button>
      {onPin && (
        <button
          type="button"
          className={cn(
            "rounded p-0.5",
            pinned
              ? "text-primary"
              : "text-muted-foreground hover:text-foreground",
          )}
          onClick={onPin}
          aria-label={
            pinned
              ? `Unpin ${label} from the rows`
              : `Pin ${label} beside the rows`
          }
          title={
            pinned
              ? "Shown beside the row labels — click to spread it across the columns"
              : "Show beside the row labels instead of under every column"
          }
        >
          <Pin className={cn("size-3", pinned && "fill-current")} />
        </button>
      )}
      {onRemove ? (
        <button
          type="button"
          className="rounded p-0.5 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          aria-label={`Remove ${label}`}
        >
          <X className="size-3" />
        </button>
      ) : (
        <span className="w-1" />
      )}
    </span>
  );
}

function ConditionChip({
  dimension,
  token,
  open,
  onOpenChange,
  values,
  onValuesChange,
  onApply,
  onRemove,
}: {
  dimension: EntryQueryDimension | undefined;
  /** Absent while the condition is still being composed. */
  token?: Condition;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: string[];
  onValuesChange: (values: string[]) => void;
  onApply: (negate: boolean) => void;
  onRemove: () => void;
}) {
  const label = dimension?.label ?? token?.field ?? "Field";
  const summary = token
    ? token.values
        .map(
          (value) =>
            dimension?.options.find((o) => o.value === value)?.label ?? value,
        )
        .slice(0, 2)
        .join(", ") +
      (token.values.length > 2 ? ` +${token.values.length - 2}` : "")
    : "pick values";

  return (
    <span className={cn(CHIP, !token && "border-dashed")}>
      <Popover modal={false} open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button type="button" className="flex items-center gap-1 text-left">
            <span className="font-medium">{label}</span>
            <span className="text-muted-foreground">
              {token?.negate ? "is none of" : "is any of"}
            </span>
            <span className="truncate">{summary}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[300px] p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ConditionValueList
            dimension={dimension}
            values={values}
            onValuesChange={onValuesChange}
            onApply={onApply}
          />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        className="rounded p-0.5 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        aria-label={`Remove the ${label} filter`}
      >
        <X className="size-3" />
      </button>
    </span>
  );
}
